import { CmpWidgetChartBase } from "./tmpls";
import { App } from "../App";
import { Helper } from "../libs/Helper";
import { DEXPriceCandle, LPXInfo, LPXSwapDirection, LPXSwapInfo } from "../api/classes/models";
import { LPXInfrastructure } from "../contracts/LPXInfrastructure";
import { LPXReserves } from "../contracts/LPXContract";
import { BiMath } from "../libs/BiMath";
import { Web3Helper } from "../libs/Web3Helper";
import { WrapperGS } from "@ivanlog/gs";

export class CmpWidgetChart extends CmpWidgetChartBase {

    private static readonly COLOR_TEXT = "#7072A2";
    private static readonly COLOR_BACKGROUND = "#080926";
    private static readonly COLOR_GRID = "#242548";
    private static readonly COLOR_WATERMARK = "rgba(112, 114, 162, 0.45)";
    private static readonly COLOR_CANDLE_UP = "#04C9A1";
    private static readonly COLOR_CANDLE_DOWN = "#B90042";
    private static readonly COLOR_TRANSPARENT = "rgba(0,0,0,0)";
    private static readonly COLOR_PRICE_LINE_BUY = "green";
    private static readonly COLOR_PRICE_LINE_SELL = "red";
    public static readonly LINE_MAX_SELL = "maxSell";
    public static readonly LINE_MIN_BUY = "minBuy";
    public static readonly LINE_SELL = "sell";
    public static readonly LINE_BUY = "buy";
   // private static readonly LPX_INTERVAL = "15m";
    private static readonly DEFAULT_INTERVAL = "5min";
    private static readonly LPX_CANDLES_COUNT = 900;
    private static readonly SWAPS_COUNT = 1000;

    private _cache:Map<string,DEXPriceCandle[]>=new Map<string,DEXPriceCandle[]>();
    private _tradesCache:Map<number,LPXSwapInfo[]>=new Map<number,LPXSwapInfo[]>();
    private _chart:any;
    private _candlestickSeries:any;
    private _crosshairMoveHandler:any;
    private _loadVersion:number = 0;
    private _lines:Map<string,any> =new Map<string,any>();

    private _interval:string="15m";
    private _typeBtns:WrapperGS[]=[];
    private _pool:LPXInfo|undefined;
    private _fundAdr:string="";
    private _anchorAdr:string="";
    private _lastClosePrice:number|undefined;
    public get lastClosePrice(){ return this._lastClosePrice; }

    constructor() {
        super();
        void this._loadAsync();

        this._typeBtns = this.q('.btns a').all;
        for(const btn of this._typeBtns)
        {
            if (btn.getData('interval')==this._interval) btn.disable(); else btn.enable();
            btn.onclick=()=>{
                this._typeBtns.forEach(b=>b.enable());
                btn.disable();
                this._interval = btn.getData('interval');
                this._reloadAsync();
            }
        }
    }

    private _disposeChart()
    {
        this.ctrls.lText.text = "";
        if (this._chart?.unsubscribeCrosshairMove && this._crosshairMoveHandler)
            this._chart.unsubscribeCrosshairMove(this._crosshairMoveHandler);
        if (this._chart?.remove) this._chart.remove();
        this._chart = undefined;
        this._candlestickSeries = undefined;
        this._crosshairMoveHandler = undefined;
        this._lines.clear();
        this._lastClosePrice = undefined;
    }

    private _createChart()
    {
        const container = this.ctrls.chart;
        if (!container) return;

        const chartH = container.getData("height");
        if (chartH) container.el.style.height=chartH;
        this._disposeChart();
        container.html='';

        const chart = globalThis.LightweightCharts.createChart(container.el, {
            autoSize:true,
            layout: {
                textColor: CmpWidgetChart.COLOR_TEXT,
                background: { type: "solid", color: CmpWidgetChart.COLOR_BACKGROUND }
            },
            timeScale: {
                timeVisible: true,
                secondsVisible: false,
            },
            grid: {
                vertLines: {
                    color: CmpWidgetChart.COLOR_GRID,
                    style: 0,
                },
                horzLines: {
                    color: CmpWidgetChart.COLOR_GRID,
                    style: 0,
                },
            }
        });
        chart.applyOptions({
            watermark: {
                visible: true,
                text: "Loading...",
                color: CmpWidgetChart.COLOR_WATERMARK,
                fontSize: 24,
                horzAlign: "center",
                vertAlign: "center"
            }
        });
        const candlestickSeries = chart.addCandlestickSeries({
            upColor: CmpWidgetChart.COLOR_CANDLE_UP,
            downColor: CmpWidgetChart.COLOR_CANDLE_DOWN,
            borderVisible: false,
            wickUpColor: CmpWidgetChart.COLOR_CANDLE_UP,
            wickDownColor: CmpWidgetChart.COLOR_CANDLE_DOWN,
            priceFormat: {
                type: "price",
                minMove: 0.0000001
            }
        });

        this._chart=chart;
        this._candlestickSeries=candlestickSeries;
        return { chart, candlestickSeries };
    }

    private _getPairCacheKey(fundAdr:string, anchorAdr:string)
    {
        return fundAdr.toLowerCase() + anchorAdr.toLowerCase() + this._interval;
    }

    private _getPoolCacheKey(pool:LPXInfo)
    {
        return this._getPairCacheKey(pool.fundToken.erc20Address, pool.anchorToken.erc20Address);
    }

    private _isActiveChart(chart:any, loadVersion:number)
    {
        return this._chart === chart && this._loadVersion === loadVersion;
    }

    async _reloadAsync()
    {
        if (this._pool) this.loadLPXAsync(this._pool);
        else if (this._fundAdr&&this._anchorAdr) this.loadPairAsync(this._fundAdr, this._anchorAdr);
        else this._loadAsync();
    }

    async loadPairAsync(fundAdr:string,anchorAdr:string)
    {
        const loadVersion = ++this._loadVersion;

        await Helper.awaitTime(100);
        if (loadVersion !== this._loadVersion) return;

        const chartData = this._createChart();
        if (!chartData) return;
        const { chart, candlestickSeries } = chartData;

        this._fundAdr = fundAdr;
        this._anchorAdr = anchorAdr;

        const key = this._getPairCacheKey(fundAdr, anchorAdr);
        try
        {
            if (!this._cache.has(key))
            {
                const dataResponse = await App.api.dEXWatcher.getCandles(
                    anchorAdr,
                    fundAdr,
                    this._interval,
                    CmpWidgetChart.LPX_CANDLES_COUNT
                );
                if (!this._isActiveChart(chart, loadVersion)) return;
                if (!dataResponse || dataResponse.length == 0) return;
                if (dataResponse.length > 1) dataResponse[0]=dataResponse[1];
                for(const d of dataResponse) (d as any).time=d.openTS;
                this._cache.set(key, dataResponse);
            }
            if (!this._isActiveChart(chart, loadVersion)) return;
            const candles = this._cache.get(key);
            if (!candles) return;
            candlestickSeries.setData(candles);
            this._lastClosePrice = candles[candles.length - 1]?.close;
            if (!this._isActiveChart(chart, loadVersion)) return;
            chart.timeScale().fitContent();
        } finally
        {
            if (this._isActiveChart(chart, loadVersion))
                chart.applyOptions({
                    watermark: {
                        visible: false
                    }
                });
        }
    }

    async loadLPXAsync(pool:LPXInfo)
    {
        this._pool=pool;
        const lpxInf=App.Inst.getInst(LPXInfrastructure);
        await this.loadPairAsync(pool.fundToken.erc20Address, pool.anchorToken.erc20Address);
        const chart = this._chart;
        if (lpxInf)
        {
            const reserves = LPXReserves.FromLPXInfo(pool);
            const pack = lpxInf.unpackConfigPack(BiMath.WEIToBI(pool.packRaw));
            const fundDecimal = BiMath.Pow10(pool.fundToken.decimals);
            const anchorDecimal = BiMath.Pow10(pool.anchorToken.decimals);
            const pairDecimalCor = fundDecimal*BiMath.Decimal/anchorDecimal;

            if (reserves.fundImgReserve>0)
            {
                const priceEnd = lpxInf.calcEndRatio(reserves, pack.feeIndex)
                *pairDecimalCor/BiMath.Decimal;
                this.setLinePrice(CmpWidgetChart.LINE_MAX_SELL, parseFloat(BiMath.ToEth(priceEnd)));
            }
            if (reserves.anchorImgReserve>0)
            {
                const priceEnd = lpxInf.calcStartRatio(reserves, pack.feeIndex)
                *(BiMath.Decimal17B-pack.ntzIndex)/BiMath.Decimal17B
                *pairDecimalCor/BiMath.Decimal;
                this.setLinePrice(CmpWidgetChart.LINE_MIN_BUY, parseFloat(BiMath.ToEth(priceEnd)));
            }
            if (reserves.fundReserve + reserves.fundImgReserve>0)
            {
                const priceSell = lpxInf.calcCurrentRatio(reserves)
                *pairDecimalCor/BiMath.Decimal;
                this.setLinePrice(CmpWidgetChart.LINE_SELL, parseFloat(BiMath.ToEth(priceSell)));

                const priceBuy = lpxInf.calcCurrentRatio(reserves)
                *(BiMath.Decimal17B-pack.ntzIndex)/BiMath.Decimal17B
                *pairDecimalCor/BiMath.Decimal;
                this.setLinePrice(CmpWidgetChart.LINE_BUY, parseFloat(BiMath.ToEth(priceBuy)));
            }
        }
        await this._loadTradesAsync(pool, chart);
    }


    public setLinePrice(lineName:string, price:number)
    {
        if (!this._candlestickSeries) return;

        const line = this._lines.get(lineName);
        if (line)
        {
            this._candlestickSeries.removePriceLine(line);
            this._lines.delete(lineName);
        }
        if (price==0) return;
        if (lineName==CmpWidgetChart.LINE_MAX_SELL) this._addPriceLine(lineName, price,"MaxSell", CmpWidgetChart.COLOR_PRICE_LINE_SELL, 2);
        else if (lineName==CmpWidgetChart.LINE_MIN_BUY) this._addPriceLine(lineName, price,"MinBuy", CmpWidgetChart.COLOR_PRICE_LINE_BUY, 2);
        else if (lineName==CmpWidgetChart.LINE_SELL) this._addPriceLine(lineName, price,"Sell", CmpWidgetChart.COLOR_PRICE_LINE_SELL, 1);
        else if (lineName==CmpWidgetChart.LINE_BUY) this._addPriceLine(lineName, price,"Buy", CmpWidgetChart.COLOR_PRICE_LINE_BUY, 1);
    }

    private _addPriceLine(name:string, price:number, title:string, color:string, size:number)
    {
        if (!this._candlestickSeries) return;

        const currentLine = this._lines.get(name);
        if (currentLine)
        {
            this._candlestickSeries.removePriceLine(currentLine);
            this._lines.delete(name);
        }
        const line = this._candlestickSeries.createPriceLine({
            price: price,
            color: color,
            lineWidth: size,
            lineStyle: globalThis.LightweightCharts.LineStyle.Solid,
            axisLabelVisible: true,
            title: title
        });
        this._lines.set(name, line);
    }

    private async _loadTradesAsync(pool:LPXInfo, chart:any)
    {
        const candlestickSeries = this._candlestickSeries;
        const loadVersion = this._loadVersion;
        if (!chart || !candlestickSeries?.setMarkers) return;

        candlestickSeries.setMarkers([]);
        this.ctrls.lText.text = this._getDefaultTextLabel();

        const fundDecimal = BiMath.Pow10(pool.fundToken.decimals);
        const anchorDecimal = BiMath.Pow10(pool.anchorToken.decimals);

        let dataResponse = this._tradesCache.get(pool.poolId);
        if (!dataResponse)
        {
            dataResponse = await App.api.lPXWatcher.getSwaps(pool.poolId, CmpWidgetChart.SWAPS_COUNT, 0, 0);
            if (dataResponse) this._tradesCache.set(pool.poolId, dataResponse);
        }
        if (!this._isActiveChart(chart, loadVersion)) return;
        if (!dataResponse || dataResponse.length == 0) return;

        const candles = this._cache.get(this._getPoolCacheKey(pool)) ?? [];
        const candleTimes = candles
            .map(candle=>Number((candle as any).time ?? candle.openTS))
            .filter(time=>Number.isFinite(time))
            .sort((a, b)=>a - b);

        const markers:any[] = [];
        const valuesByCandleTime = new Map<number,{
            buyFundRaw:bigint;
            buyAnchorRaw:bigint;
            sellFundRaw:bigint;
            sellAnchorRaw:bigint;
        }>();

        for(const swap of dataResponse)
        {
            const tradeTime = Number(swap.timestamp);
            if (!Number.isFinite(tradeTime)) continue;

            let markerTime:number|undefined;
            if (candleTimes.length == 0) markerTime = tradeTime;
            else
                for(const candleTime of candleTimes)
                {
                    if (candleTime > tradeTime) break;
                    markerTime = candleTime;
                }

            if (markerTime === undefined) continue;

            const isBuyFund = swap.direction == LPXSwapDirection.InFund;
            const isSellFund = swap.direction == LPXSwapDirection.InAnchor;
            if (!isBuyFund && !isSellFund) continue;

            const position = isSellFund ? "aboveBar":"belowBar";
            const color = isSellFund ? CmpWidgetChart.COLOR_CANDLE_DOWN: CmpWidgetChart.COLOR_CANDLE_UP;
            const shape = isSellFund ? "arrowDown":"arrowUp";
            if (!valuesByCandleTime.has(markerTime))
                valuesByCandleTime.set(markerTime, {
                    buyFundRaw: 0n,
                    buyAnchorRaw: 0n,
                    sellFundRaw: 0n,
                    sellAnchorRaw: 0n
                });
            const candleValue = valuesByCandleTime.get(markerTime);
            if (!candleValue) continue;

            const fundRaw = BiMath.WEIToBI(swap.fundDeltaRaw);
            const anchorRaw = BiMath.WEIToBI(swap.anchorDeltaRaw);
            if (isBuyFund)
            {
                candleValue.buyFundRaw += fundRaw;
                candleValue.buyAnchorRaw += anchorRaw;
            } else
            {
                candleValue.sellFundRaw += fundRaw;
                candleValue.sellAnchorRaw += anchorRaw;
            }

            markers.push({
                time: markerTime,
                position: position,
                shape: "circle",
                color: CmpWidgetChart.COLOR_TRANSPARENT,
                sortOrder: 0
            });
            markers.push({
                time: markerTime,
                position: position,
                shape: shape,
                color: color,
                sortOrder: 1
            });
        }
        if (!this._isActiveChart(chart, loadVersion)) return;

        candlestickSeries.setMarkers(markers
            .sort((a, b)=>a.time - b.time || a.sortOrder - b.sortOrder)
            .map(marker=>{
                delete marker.sortOrder;
                return marker;
            }));

        if (!chart.subscribeCrosshairMove) return;
        if (this._crosshairMoveHandler && chart.unsubscribeCrosshairMove)
            chart.unsubscribeCrosshairMove(this._crosshairMoveHandler);

        this._crosshairMoveHandler = param=>{
            if (!this._isActiveChart(chart, loadVersion)) return;

            const hoveredTime = Number(param?.time);
            if (!Number.isFinite(hoveredTime))
            {
                this.ctrls.lText.text = this._getDefaultTextLabel();
                return;
            }

            const values = valuesByCandleTime.get(hoveredTime);
            if (!values)
            {
                this.ctrls.lText.text = this._getDefaultTextLabel();
                return;
            }

            const parts:string[] = [];
            if (values.buyFundRaw > 0n || values.buyAnchorRaw > 0n)
            {
                const buyFundValue = Web3Helper.ToLargeNumberUnit(values.buyFundRaw*BiMath.Decimal/fundDecimal);
                const buyAnchorValue = Web3Helper.ToLargeNumberUnit(values.buyAnchorRaw*BiMath.Decimal/anchorDecimal);
                parts.push(`Buy ${buyFundValue} ${pool.fundToken.symbol} (${buyAnchorValue} ${pool.anchorToken.symbol})`);
            }
            if (values.sellFundRaw > 0n || values.sellAnchorRaw > 0n)
            {
                const sellFundValue = Web3Helper.ToLargeNumberUnit(values.sellFundRaw*BiMath.Decimal/fundDecimal);
                const sellAnchorValue = Web3Helper.ToLargeNumberUnit(values.sellAnchorRaw*BiMath.Decimal/anchorDecimal);
                parts.push(`Sell ${sellFundValue} ${pool.fundToken.symbol} (${sellAnchorValue} ${pool.anchorToken.symbol})`);
            }
            this.ctrls.lText.text = parts.join(" | ");
        };
        chart.subscribeCrosshairMove(this._crosshairMoveHandler);
    }

    async _loadAsync()
    {
        const loadVersion = ++this._loadVersion;

        await Helper.awaitTime(500);
        if (loadVersion !== this._loadVersion) return;

        const symbol = this.ctrls.chart.getData("symbol");
        if (!symbol) return;

        const chartData = this._createChart();
        if (!chartData) return;
        const { chart, candlestickSeries } = chartData;

        try
        {
            const dataResponse = await fetch("https://plusx.app/stats/"+symbol+"/candles_"+CmpWidgetChart.DEFAULT_INTERVAL);
            if (!this._isActiveChart(chart, loadVersion)) return;

            const data = await dataResponse.json();
            if (!this._isActiveChart(chart, loadVersion)) return;

            for(const d of data) d.time=d.timestamp;
            candlestickSeries.setData(data);
            this._lastClosePrice = data[data.length - 1]?.close;
            chart.timeScale().fitContent();
        } finally
        {
            if (this._isActiveChart(chart, loadVersion))
                chart.applyOptions({
                    watermark: {
                        visible: false
                    }
                });
        }
    }

    _getDefaultTextLabel()
    {
        if (this._pool)
        {
            return "#"+this._pool.poolId+" "+this._pool.fundToken.symbol+"/"+this._pool.anchorToken.symbol;
        }
        return "";
    }
}
